import { useCallback, useSyncExternalStore } from 'react';
import { wizardStore } from '@/_shared/components/Wizard/store';

export type WizardNavigateCallback = (goToStep?: number | undefined) => void;
export type NavigateCallback = (navigate?: WizardNavigateCallback) => void;

/**
 * A custom React hook for managing wizard-related state and actions.
 * It provides functionality to handle navigation callbacks, button states, and custom logic for wizard steps.
 *
 * @returns {Object} - The hook returns an object containing:
 * - `isLoadingNextButton` (boolean): Indicates whether the "Next" button is in a loading state.
 * - `disableNextButton` (boolean): Indicates whether the "Next" button is disabled.
 * - `disablePreviousButton` (boolean): Indicates whether the "Previous" button is disabled.
 * - `disableSkipButton` (boolean): Indicates whether the "Skip" button is disabled.
 * - `handleNext` (function): A function to handle the "next" action, including any custom logic defined in the current step.
 * - `handlePrevious` (function): A function to handle the "previous" action, including any custom logic defined in the current step.
 * - `handleSkip` (function): A function to handle the "skip" action, including any custom logic defined in the current step.
 * - `setNavigationButtonStatus` (function): A function to update the status of navigation buttons.
 * - `setCustomOnNext` (function): A function to set custom logic for the "next" action.
 * - `setCustomOnPrevious` (function): A function to set custom logic for the "previous" action.
 * - `setCustomOnSkip` (function): A function to set custom logic for the "skip" action.
 * - `resetWizardNavigationCallbacks` (function): A function to reset all navigation callbacks.
 * - `resetWizardNavigationButtonStatus` (function): A function to reset the status of all navigation buttons.
 *
 * Dependencies:
 * - React's `useCallback` and `useSyncExternalStore` hooks.
 * - `wizardStore` for managing wizard-related state.
 *
 * @example
 * Example usage:
 * ```javascript
 * import { useWizard } from './useWizard';
 *
 * function AddDelegate() {
 *   const {
 *     setCustomOnNext,
 *     setCustomOnSkip,
 *     setNavigationButtonStatus,
 *     resetWizardNavigationCallbacks,
 *     resetWizardNavigationButtonStatus,
 *   } = useWizard();
 *
 *   const onSkip = useCallback((navigate) => {
 *     console.log('Custom Skip Action')
 *     navigate?.(WIZARD_STEPS.FINAL_STEP);
 *   }, []);
 *
 *   useEffect(() => {
 *     setCustomOnNext(() => console.log('Custom Next Action'));
 *     setCustomOnSkip(onSkip);
 *
 *     return () => {
 *       resetWizardNavigationCallbacks();
 *     };
 *   }, [setCustomOnNext, setCustomOnSkip, resetWizardNavigationCallbacks]);
 *
 *   useEffect(() => {
 *     setNavigationButtonStatus({
 *       disableNextButton: false,
 *       disablePreviousButton: false,
 *       disableSkipButton: false,
 *       isLoadingNextButton: false,
 *     });
 *
 *     return () => {
 *       resetWizardNavigationButtonStatus();
 *     };
 *   }, [setNavigationButtonStatus, resetWizardNavigationButtonStatus]);
 *
 *   return <div>Component</div>;
 * }
 * ```
 */
export const useWizard = () => {
  const isLoadingNextButton = useSyncExternalStore(
    wizardStore.subscribe,
    () => wizardStore.getState().isLoadingNextButton,
  );
  const disableNextButton = useSyncExternalStore(
    wizardStore.subscribe,
    () => wizardStore.getState().disableNextButton,
  );
  const disablePreviousButton = useSyncExternalStore(
    wizardStore.subscribe,
    () => wizardStore.getState().disablePreviousButton,
  );
  const disableSkipButton = useSyncExternalStore(
    wizardStore.subscribe,
    () => wizardStore.getState().disableSkipButton,
  );

  const onNext = useSyncExternalStore(wizardStore.subscribe, () => wizardStore.getState().onNext);
  const onPrevious = useSyncExternalStore(
    wizardStore.subscribe,
    () => wizardStore.getState().onPrevious,
  );
  const onSkip = useSyncExternalStore(wizardStore.subscribe, () => wizardStore.getState().onSkip);

  const setCustomOnNext = useCallback(
    (callback?: NavigateCallback) => wizardStore.setOnNext(callback),
    [],
  );
  const setCustomOnPrevious = useCallback(
    (callback?: NavigateCallback) => wizardStore.setOnPrevious(callback),
    [],
  );
  const setCustomOnSkip = useCallback(
    (callback?: NavigateCallback) => wizardStore.setOnSkip(callback),
    [],
  );

  const handleNext = useCallback(
    (navigate?: WizardNavigateCallback) => {
      if (onNext) {
        onNext(navigate);
      } else {
        navigate?.();
      }
    },
    [onNext],
  );

  const handlePrevious = useCallback(
    (navigate?: WizardNavigateCallback) => {
      if (onPrevious) {
        onPrevious(navigate);
      } else {
        navigate?.();
      }
    },
    [onPrevious],
  );

  const handleSkip = useCallback(
    (onNavigate?: WizardNavigateCallback) => {
      if (onSkip) {
        onSkip(onNavigate);
      } else {
        onNavigate?.();
      }
    },
    [onSkip],
  );

  const setNavigationButtonStatus = useCallback(
    (status: {
      isLoadingNextButton?: boolean;
      disableNextButton?: boolean;
      disablePreviousButton?: boolean;
      disableSkipButton?: boolean;
    }) => {
      if (status.isLoadingNextButton !== undefined) {
        wizardStore.setIsLoadingNextButton(status.isLoadingNextButton);
      }
      if (status.disableNextButton !== undefined) {
        wizardStore.setDisableNextButton(status.disableNextButton);
      }
      if (status.disablePreviousButton !== undefined) {
        wizardStore.setDisablePreviousButton(status.disablePreviousButton);
      }
      if (status.disableSkipButton !== undefined) {
        wizardStore.setDisableSkipButton(status.disableSkipButton);
      }
    },
    [],
  );

  return {
    isLoadingNextButton,
    disableNextButton,
    disablePreviousButton,
    disableSkipButton,
    handleNext,
    handlePrevious,
    handleSkip,
    setNavigationButtonStatus,
    setCustomOnNext,
    setCustomOnPrevious,
    setCustomOnSkip,
    resetWizardNavigationCallbacks: wizardStore.resetCallbacks,
    resetWizardNavigationButtonStatus: wizardStore.resetButtons,
  };
};
